import './CreateReviewButton.scss';

import { User } from 'mission-control-common-components';
import React, { useCallback } from 'react';

import Button from '../../../common/Button';
import Tooltip from '../../../common/Tooltip';
import type { PermissionsNames } from '../../../constants';
import { TraffickingTableName } from '../../../constants';
import bem from '../../../utils/bem';
import { singularTitleCase } from '../../../utils/formatting';
import type { AdModel, EntityModel, LineItemModel } from '../TraffickingPage/modelConverters';
import CreateReviewLinkModal from './CreateReviewLinkModal/CreateReviewLinkModal';
import useCreateReviewButton from './hooks';
import type { FlatRowType, ReviewTableName } from './types';

const [block, element] = bem('create-review-button');

export interface CreateReviewButtonProps {
  tableName: ReviewTableName;
  selectedFlatRows: FlatRowType<EntityModel>;
  selectedAdRows?: AdModel[];
  selectedLineItemRows?: LineItemModel[];
}

function CreateReviewButton({
  selectedFlatRows,
  tableName,
  selectedAdRows,
  selectedLineItemRows,
}: CreateReviewButtonProps): JSX.Element {
  const permissions = User.getPermissions<PermissionsNames>();

  const { reviewPath, disabledButton, showModal, setShowModal, useCampaignReviewer } = useCreateReviewButton({
    tableName,
    selectedAdRows,
    selectedLineItemRows,
  });

  const handleClick = useCallback((): void => {
    if (useCampaignReviewer) {
      window.open(reviewPath, '_blank');
    } else {
      setShowModal((showModal: boolean) => !showModal);
    }
  }, [useCampaignReviewer, reviewPath, setShowModal]);

  const hasAccessToCreateReviewLink =
    tableName === TraffickingTableName.campaigns
      ? permissions?.createCampaignReviewLink
      : permissions?.createLineItemReviewLink;

  const tooltipMessage = useCampaignReviewer
    ? `Select ${singularTitleCase(tableName).toLowerCase()}s to submit for review.`
    : `Select ${singularTitleCase(tableName).toLowerCase()}s to create a review link.`;

  return (
    <>
      <Tooltip
        id={`create-${tableName}-link`}
        className={element('tooltip')}
        message={tooltipMessage}
        disable={!disabledButton}
      >
        <Button
          size="medium"
          icon={useCampaignReviewer ? 'IconDocument' : 'IconLink'}
          variant="outlined"
          className={block()}
          onClick={handleClick}
          disabled={!hasAccessToCreateReviewLink || disabledButton}
          aria-label={useCampaignReviewer ? 'Submit for Review' : 'Create Review Link'}
          data-testid="create-review-button"
        >
          {useCampaignReviewer ? 'Submit for Review' : 'Create Review Link'}
        </Button>
      </Tooltip>
      {showModal && !useCampaignReviewer && (
        <CreateReviewLinkModal
          reviewPath={reviewPath}
          closeModal={handleClick}
          tableName={tableName}
          selectedFlatRows={selectedFlatRows}
        />
      )}
    </>
  );
}

export default CreateReviewButton;
